import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { MCP_REGISTRY } from "../data/mcpRegistry";
import type { SessionUser } from "./sessionUser";
import type { RegistryComment, RegistryDetails, RegistryItem } from "./registryApi";
import {
  deleteRegistryComment,
  editRegistryComment,
  fetchRegistryDetails,
  fetchRegistryList,
  postRegistryComment,
  registerRegistryDownload,
  toggleRegistryStar,
} from "./registryApi";

export const registryKeys = {
  all: ["registry"] as const,
  list: () => [...registryKeys.all, "list"] as const,
  detail: (slug: string) => [...registryKeys.all, "detail", slug] as const,
};

const registryQueryOptions = {
  staleTime: 60_000,          // show cached data immediately; silently refetch after 60s
  refetchOnMount: true,
  refetchOnWindowFocus: false, // don't blast the API on every tab switch
} as const;

const registryDetailQueryOptions = {
  staleTime: 30_000,
  refetchOnMount: true,
  refetchOnWindowFocus: false,
} as const;

const suppressedCommentIds = new Map<string, Set<string>>();

function suppressComment(slug: string, commentId: string) {
  const existing = suppressedCommentIds.get(slug);
  if (existing) {
    existing.add(commentId);
    return;
  }
  suppressedCommentIds.set(slug, new Set([commentId]));
}

function unsuppressComment(slug: string, commentId: string) {
  const existing = suppressedCommentIds.get(slug);
  if (!existing) return;
  existing.delete(commentId);
  if (existing.size === 0) suppressedCommentIds.delete(slug);
}

function reconcileSuppressedComments(slug: string, comments: readonly RegistryComment[]) {
  const suppressed = suppressedCommentIds.get(slug);
  if (!suppressed?.size) return;
  const liveIds = new Set(comments.map((comment) => comment.id));
  for (const commentId of suppressed) {
    if (!liveIds.has(commentId)) suppressed.delete(commentId);
  }
  if (suppressed.size === 0) suppressedCommentIds.delete(slug);
}

function applyCommentSuppressions(slug: string, details: RegistryDetails): RegistryDetails {
  const suppressed = suppressedCommentIds.get(slug);
  if (!suppressed?.size) return details;
  const comments = details.comments.filter((comment) => !suppressed.has(comment.id));
  if (comments.length === details.comments.length) return details;
  return { ...details, comments };
}

export function sortRegistryByEngagement(items: readonly RegistryItem[]): RegistryItem[] {
  return [...items].sort((a, b) => {
    if (b.starCount !== a.starCount) return b.starCount - a.starCount;
    return b.downloadCount - a.downloadCount;
  });
}

export function localRegistryFallback(): RegistryItem[] {
  return sortRegistryByEngagement(
    MCP_REGISTRY.map((mcp) => ({
      id: mcp.slug,
      name: mcp.name,
      slug: mcp.slug,
      description: mcp.description,
      category: mcp.category,
      icon: mcp.icon,
      fileUrl: mcp.downloadUrl,
      downloadCount: mcp.downloads,
      starCount: mcp.stars,
      createdAt: mcp.createdAt,
      updatedAt: mcp.updatedAt,
      commentCount: mcp.comments.length,
      isStarred: false,
      endpointCount: mcp.endpointCount ?? 0,
      // Surface auth metadata from local catalog so cards/detail show correct UX
      // even before the API responds (or when it's unreachable).
      ...(mcp.authType ? { authType: mcp.authType } : {}),
      ...(mcp.authLabel ? { authLabel: mcp.authLabel } : {}),
    })),
  );
}

function patchRegistryList(
  items: RegistryItem[] | undefined,
  slug: string,
  patch: Partial<RegistryItem>,
): RegistryItem[] | undefined {
  if (!items) return items;
  return sortRegistryByEngagement(
    items.map((item) => (item.slug === slug ? { ...item, ...patch } : item)),
  );
}

function patchRegistryDetail(
  details: RegistryDetails | undefined,
  patch: Partial<RegistryDetails>,
): RegistryDetails | undefined {
  if (!details) return details;
  return { ...details, ...patch };
}

async function refreshRegistryCache(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.refetchQueries({ queryKey: registryKeys.all, type: "all" });
}

export function useRegistryList() {
  return useQuery({
    queryKey: registryKeys.list(),
    queryFn: async ({ signal }) => {
      try {
        const items = await fetchRegistryList({ signal });
        return items.length > 0 ? items : localRegistryFallback();
      } catch (error) {
        if (signal.aborted) throw error;
        return localRegistryFallback();
      }
    },
    select: sortRegistryByEngagement,
    ...registryQueryOptions,
  });
}

export function useRegistryDetails(slug: string) {
  return useQuery({
    queryKey: registryKeys.detail(slug),
    queryFn: async ({ signal }) => {
      try {
        const details = await fetchRegistryDetails(slug, { signal });
        reconcileSuppressedComments(slug, details.comments);
        return details;
      } catch (error) {
        if (signal.aborted) throw error;
        const fallback = localRegistryFallback().find((item) => item.slug === slug);
        const catalogEntry = MCP_REGISTRY.find((mcp) => mcp.slug === slug);
        if (!fallback) throw new Error("MCP not found");
        return {
          ...fallback,
          comments: [],
          features: catalogEntry?.features,
          linkOnlyUrl: catalogEntry?.linkOnlyUrl,
          authType: catalogEntry?.authType,
          authLabel: catalogEntry?.authLabel,
          authInstructions: catalogEntry?.authInstructions,
          credentialKey: catalogEntry?.credentialKey,
          credentialHeader: catalogEntry?.credentialHeader,
        };
      }
    },
    select: (details) => applyCommentSuppressions(slug, details),
    enabled: Boolean(slug),
    ...registryDetailQueryOptions,
  });
}

export function useToggleRegistryStar(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => toggleRegistryStar(slug),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: registryKeys.detail(slug) });
      await queryClient.cancelQueries({ queryKey: registryKeys.list() });

      const previousDetail = queryClient.getQueryData<RegistryDetails>(registryKeys.detail(slug));
      const previousList = queryClient.getQueryData<RegistryItem[]>(registryKeys.list());
      const current = previousDetail ?? previousList?.find((item) => item.slug === slug);
      const nextIsStarred = !current?.isStarred;
      const nextStarCount = Math.max(0, (current?.starCount ?? 0) + (nextIsStarred ? 1 : -1));

      queryClient.setQueryData(registryKeys.detail(slug), (currentDetail: RegistryDetails | undefined) =>
        patchRegistryDetail(currentDetail, {
          isStarred: nextIsStarred,
          starCount: nextStarCount,
        }),
      );
      queryClient.setQueryData(registryKeys.list(), (currentList: RegistryItem[] | undefined) =>
        patchRegistryList(currentList, slug, {
          isStarred: nextIsStarred,
          starCount: nextStarCount,
        }),
      );

      return { previousDetail, previousList };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(registryKeys.detail(slug), context.previousDetail);
      }
      if (context?.previousList !== undefined) {
        queryClient.setQueryData(registryKeys.list(), context.previousList);
      }
    },
    onSuccess: (result) => {
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        patchRegistryDetail(current, {
          isStarred: result.isStarred,
          starCount: result.starCount,
        }),
      );
      queryClient.setQueryData(registryKeys.list(), (current: RegistryItem[] | undefined) =>
        patchRegistryList(current, slug, {
          isStarred: result.isStarred,
          starCount: result.starCount,
        }),
      );
    },
  });
}

export function useRegisterRegistryDownload(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => registerRegistryDownload(slug),
    onSuccess: async (result) => {
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        patchRegistryDetail(current, { downloadCount: result.downloadCount }),
      );
      queryClient.setQueryData(registryKeys.list(), (current: RegistryItem[] | undefined) =>
        patchRegistryList(current, slug, { downloadCount: result.downloadCount }),
      );
      await refreshRegistryCache(queryClient);
    },
  });
}

function upsertRegistryComment(
  details: RegistryDetails | undefined,
  comment: RegistryComment,
): RegistryDetails | undefined {
  if (!details) return details;
  const comments = details.comments.some((item) => item.id === comment.id)
    ? details.comments.map((item) => (item.id === comment.id ? comment : item))
    : [comment, ...details.comments];
  return { ...details, comments };
}

function removeRegistryComment(
  details: RegistryDetails | undefined,
  commentId: string,
): RegistryDetails | undefined {
  if (!details) return details;
  if (!details.comments.some((comment) => comment.id === commentId)) return details;
  return { ...details, comments: details.comments.filter((comment) => comment.id !== commentId) };
}

export function usePostRegistryComment(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ content, user }: { content: string; user: SessionUser }) =>
      postRegistryComment(slug, content, user),
    onSuccess: (comment) => {
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        upsertRegistryComment(current, comment),
      );
    },
  });
}

export function useEditRegistryComment(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, content, user }: { commentId: string; content: string; user: SessionUser }) =>
      editRegistryComment(slug, commentId, content, user),
    onSuccess: (comment) => {
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        upsertRegistryComment(current, comment),
      );
    },
  });
}

export function useDeleteRegistryComment(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, user }: { commentId: string; user: SessionUser }) =>
      deleteRegistryComment(slug, commentId, user),
    onMutate: async ({ commentId }) => {
      suppressComment(slug, commentId);
      await queryClient.cancelQueries({ queryKey: registryKeys.detail(slug) });
      const previousDetail = queryClient.getQueryData<RegistryDetails>(registryKeys.detail(slug));
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        removeRegistryComment(current, commentId),
      );
      return { previousDetail, commentId };
    },
    onSuccess: (_result, { commentId }) => {
      queryClient.setQueryData(registryKeys.detail(slug), (current: RegistryDetails | undefined) =>
        removeRegistryComment(current, commentId),
      );
    },
    onError: (_err, { commentId }, context) => {
      unsuppressComment(slug, commentId);
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(registryKeys.detail(slug), context.previousDetail);
      }
    },
  });
}
